import React, { useState, useEffect } from 'react';
import { Link, useParams, useHistory, Prompt } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Form, Alert } from 'react-bootstrap';
import Swal from 'sweetalert2';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import PageTitle from '../../../layouts/PageTitle';
import ModernContractDetailsComponent from './ModernContractDetailsComponent';
import {
  ArrowLeft,
  Save,
  X,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Briefcase,
  Building2,
  DollarSign,
  MapPin,
  Users,
  Clock,
  FileText
} from 'lucide-react';

// Reusable InputField component
const InputField = ({
  icon: Icon,
  label,
  name,
  type = 'text',
  required = false,
  placeholder = '',
  description = null,
  options = null,
  formik,
  showValidation
}) => (
  <div className="input-group">
    <div className="input-label-container">
      <label className="input-label">
        {label}
        {required && <span className="required-indicator">*</span>}
      </label>
      {description && (
        <p className="input-description">{description}</p>
      )}
    </div>
    <div className="input-container">
      <div className="input-icon">
        <Icon size={16} />
      </div>
      {options ? (
        <Form.Select
          name={name}
          value={formik.values[name]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          className={`modern-input ${formik.touched[name] && formik.errors[name] ? 'error' : ''}`}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      ) : (
        <Form.Control
          type={type}
          name={name}
          value={formik.values[name]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={placeholder}
          className={`modern-input ${formik.touched[name] && formik.errors[name] ? 'error' : ''}`}
          as={type === 'textarea' ? 'textarea' : 'input'}
          rows={type === 'textarea' ? 4 : undefined}
        />
      )}
    </div>
    {showValidation && formik.touched[name] && formik.errors[name] && (
      <div className="error-message">
        <AlertTriangle size={14} />
        <span>{formik.errors[name]}</span>
      </div>
    )}
  </div>
);

const NewContract = () => {
  const { companyId, id } = useParams();
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  // const [departments, setDepartments] = useState([]); // Temporarily commented out
  // const [positions, setPositions] = useState([]); // Temporarily commented out
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  const formik = useFormik({
    initialValues: {
      // Basic contract information
      startdate: '',
      employmenttype: '',
      salary: '',
      salaryperiod: 'Advance',
      workweek: '40',
      employmentextent: '',
      parttimePercentage: '',
      
      // Department and position
      department_id: '',
      position_id: '',
      
      // Benefits and conditions
      vacation_days: '25',
      notice_period_weeks: '4',
      probation_period_months: '',
      
      // Tax information
      taxtable: '33',
      taxcolumn: '1',
      
      // Workplace information
      workplace: 'Other',
      workplaceAddress: '',
      workplaceCity: '',
      
      // Terms and agreements
      otherterms: '',
      accordingtocollectiveagreement: true,

      // Payroll classification
      high_risk_protection: false,
      worker_category: 'tjansteman',
    },
    validationSchema: Yup.object({
      // Basic contract validation
      startdate: Yup.date().required('Startdatum är obligatoriskt'),
      employmenttype: Yup.string().required('Anställningsform är obligatoriskt'),
      salary: Yup.number()
        .typeError('Lön måste vara en siffra')
        .positive('Lön måste vara större än 0')
        .required('Lön är obligatoriskt'),
      salaryperiod: Yup.string().required('Löneperiod är obligatoriskt'),
      workweek: Yup.number()
        .typeError('Arbetsvecka måste vara en siffra')
        .min(1, 'Arbetsvecka måste vara minst 1 timme')
        .max(60, 'Arbetsvecka kan inte överstiga 60 timmar')
        .required('Arbetsvecka är obligatoriskt'),
      employmentextent: Yup.string().required('Omfattning är obligatoriskt'),

      // Department and position validation - Temporarily commented out
      // department_id: Yup.string().required('Avdelning är obligatoriskt'),
      // position_id: Yup.string().required('Position är obligatoriskt'),

      // Benefits validation
      vacation_days: Yup.number()
        .typeError('Semesterdagar måste vara en siffra')
        .min(0, 'Semesterdagar kan inte vara negativt')
        .max(50, 'Semesterdagar kan inte överstiga 50')
        .required('Semesterdagar är obligatoriskt'),
      notice_period_weeks: Yup.number()
        .typeError('Uppsägningstid måste vara en siffra')
        .min(0, 'Uppsägningstid kan inte vara negativt')
        .max(24, 'Uppsägningstid kan inte överstiga 24 veckor')
        .required('Uppsägningstid är obligatoriskt'),
      probation_period_months: Yup.number()
        .typeError('Provanställning måste vara en siffra')
        .min(0, 'Provanställning kan inte vara negativt')
        .max(12, 'Provanställning kan inte överstiga 12 månader')
        .nullable(),

      // Tax validation - Temporarily commented out
      // taxtable: Yup.string().required('Skattetabell är obligatoriskt'),
      // taxcolumn: Yup.string().required('Skattekollumn är obligatoriskt'),

      // Workplace validation
      workplace: Yup.string().required('Tjänsteställe är obligatoriskt'),
      workplaceAddress: Yup.string().required('Arbetsplatsens adress är obligatorisk'),
      workplaceCity: Yup.string().required('Arbetsplatsens ort är obligatorisk'),
      
      // Part-time validation
      parttimePercentage: Yup.number()
        .nullable()
        .when('employmentextent', {
          is: 'Part-time',
          then: Yup.number()
            .required('Ange procent för deltid')
            .min(1, 'Procenten måste vara minst 1%')
            .max(99, 'Procenten kan inte överstiga 99%'),
          otherwise: Yup.number().nullable(),
        }),
      
      // Optional fields
      otherterms: Yup.string(),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setShowValidation(true);

      try {
        const formattedValues = {
          ...values,
          workplace: {
            name: values.workplace,
            address: values.workplaceAddress,
            city: values.workplaceCity
          }
        };

        // Remove the individual workplace fields
        delete formattedValues.workplaceAddress;
        delete formattedValues.workplaceCity;

        // Convert string values to appropriate types
        formattedValues.salary = parseFloat(formattedValues.salary);
        formattedValues.workweek = parseFloat(formattedValues.workweek);
        formattedValues.vacation_days = parseInt(formattedValues.vacation_days);
        formattedValues.notice_period_weeks = parseInt(formattedValues.notice_period_weeks);
        
        if (formattedValues.probation_period_months) {
          formattedValues.probation_period_months = parseInt(formattedValues.probation_period_months);
        }

        if (formattedValues.parttimePercentage) {
          formattedValues.parttimePercentage = parseFloat(formattedValues.parttimePercentage);
        }

        const createEmployeeContractApiUrl = getApiUrl(
          `${companyId}/hr/employees/${id}/contracts`
        );

        const response = await axios.post(
          createEmployeeContractApiUrl,
          formattedValues,
          { withCredentials: true }
        );

        if (response.data.status === 'success') {
          setIsSuccessfullySubmitted(true);
          
          // Build warning message if there are adjustments or warnings
          let warningHtml = '';
          if (response.data.adjustedContracts && response.data.adjustedContracts.length > 0) {
            warningHtml += '<p class="text-start mb-2"><strong>Justerade kontrakt:</strong></p><ul class="text-start">';
            response.data.adjustedContracts.forEach(adj => {
              warningHtml += `<li>Kontrakt från ${adj.old_valid_from} har fått sitt slutdatum ändrat till ${adj.new_valid_to}</li>`;
            });
            warningHtml += '</ul>';
          }
          
          if (response.data.warnings && response.data.warnings.length > 0) {
            warningHtml += '<p class="text-start mb-2"><strong>Varningar:</strong></p><ul class="text-start">';
            response.data.warnings.forEach(warning => {
              warningHtml += `<li>${warning}</li>`;
            });
            warningHtml += '</ul>';
          }

          if (warningHtml) {
            await Swal.fire({
              icon: 'success',
              title: 'Kontraktet har skapats!',
              html: warningHtml,
              confirmButtonText: 'OK'
            });
          } else {
            await Swal.fire({
              icon: 'success',
              title: 'Sparat!',
              text: 'Kontraktet har skapats framgångsrikt.',
              timer: 1500
            });
          }
          
          history.push(`/${companyId}/employee/${id}`);
        }
      } catch (error) {
        console.error('Error creating contract:', error);
        const errorMessage = error.response?.data?.message || 'Ett fel inträffade vid sparande av kontraktet.';
        await Swal.fire({
          icon: 'error',
          title: 'Fel',
          text: errorMessage
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Load employee data
  const loadEmployee = async () => {
    try {
      const getEmployeeApiUrl = getApiUrl(`${companyId}/hr/employees/${id}`);
      const result = await axios.get(getEmployeeApiUrl, { withCredentials: true });
      setEmployee(result.data.data || result.data);
    } catch (error) {
      console.error('Error loading employee:', error);
    }
  };

  // Temporarily commented out - Organization section not in use
  // // Load departments
  // const loadDepartments = async () => {
  //   try {
  //     const getDepartmentsApiUrl = getApiUrl(`${companyId}/hr/departments`);
  //     const result = await axios.get(getDepartmentsApiUrl, { withCredentials: true });
  //     if (result.data.status === 'success') {
  //       setDepartments(result.data.data);
  //     }
  //   } catch (error) {
  //     console.error('Error loading departments:', error);
  //   }
  // };

  // // Load positions
  // const loadPositions = async () => {
  //   try {
  //     const getPositionsApiUrl = getApiUrl(`${companyId}/hr/positions`);
  //     const result = await axios.get(getPositionsApiUrl, { withCredentials: true });
  //     if (result.data.status === 'success') {
  //       setPositions(result.data.data);
  //     }
  //   } catch (error) {
  //     console.error('Error loading positions:', error);
  //   }
  // };

  // Load all initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadEmployee() /* , loadDepartments(), loadPositions() */]); // Temporarily commented out
      setLoading(false);
    };
    loadData();
  }, [companyId, id]);

  // Handle form dirty state for navigation warnings
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (formik.dirty && !isSuccessfullySubmitted) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formik.dirty, isSuccessfullySubmitted]);

  const handleCancel = async () => {
    if (formik.dirty && !isSuccessfullySubmitted) {
      const result = await Swal.fire({
        title: 'Är du säker?',
        text: 'Du har osparade ändringar som kommer att gå förlorade!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ja, avbryt!',
        cancelButtonText: 'Nej, fortsätt redigera'
      });

      if (result.isConfirmed) {
        history.push(`/${companyId}/employee/${id}`);
      }
    } else {
      history.push(`/${companyId}/employee/${id}`);
    }
  };

  // Form validation state
  const hasErrors = Object.keys(formik.errors).length > 0;
  const isFormReadyToSubmit = () => {
    const requiredFields = [
      'startdate', 'employmenttype', 'salary', 'salaryperiod', 'workweek',
      'employmentextent', /* 'department_id', 'position_id', */ 'vacation_days',
      'notice_period_weeks', /* 'taxtable', 'taxcolumn', */ 'workplace',
      'workplaceAddress', 'workplaceCity'
    ];

    const allFieldsFilled = requiredFields.every(field => {
      const value = formik.values[field];
      return value !== null && value !== undefined && value !== '';
    });

    const partTimeValid = formik.values.employmentextent !== 'Part-time' ||
      (formik.values.parttimePercentage && formik.values.parttimePercentage > 0);

    const noErrors = Object.keys(formik.errors).length === 0;

    return allFieldsFilled && partTimeValid && noErrors;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Laddar...</span>
          </div>
          <div className="mt-2 text-muted">Laddar formulärdata...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Nytt kontrakt" motherMenu="Personal" />

      <Prompt
        when={formik.dirty && !isSuccessfullySubmitted}
        message="Du har osparade ändringar. Är du säker på att du vill lämna?"
      />

      <div>
        <div className="d-flex mb-3">
          <div className="mb-3 align-items-center me-auto">
            <h4 className="fs-24 font-w800">Lägg till kontrakt</h4>
          </div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to={`/${companyId}/employees`}>Personal</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to={`/${companyId}/employee/${id}`}>Anställd</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Nytt kontrakt
              </li>
            </ol>
          </nav>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <ModernContractDetailsComponent 
                    formik={formik} 
                    companyId={companyId}
                    showValidation={showValidation}
                  />
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      Avbryt
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting || !isFormReadyToSubmit()}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Sparar...
                        </>
                      ) : (
                        'Spara'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default NewContract;